/**
 * Rich ERC-8004 AgentCard definitions for seed agents.
 *
 * Each agent has a full AgentCard matching the 8004scan.io registration
 * schema with services, domains, trust mechanisms, and OASF capabilities.
 *
 * Local types mirror `@gotts.ai/ui` AgentCard types — devenv can't
 * import from ui package.
 */

// ---------------------------------------------------------------------------
// Local AgentCard types (mirrors packages/ui/src/devtools/types.ts)
// ---------------------------------------------------------------------------

export type AgentServiceType =
  | "mcp"
  | "a2a"
  | "oasf"
  | "custom"
  | "ens"
  | "did"
  | "agentWallet"
  | "web"
  | "email";

export interface McpServiceEndpoint {
  type: "mcp";
  url: string;
  tools?: string[];
  prompts?: string[];
  resources?: string[];
}

export interface A2aServiceEndpoint {
  type: "a2a";
  url: string;
  a2aSkills?: string[];
}

export interface OasfServiceEndpoint {
  type: "oasf";
  url: string;
  skills?: string[];
  domains?: string[];
}

export interface CustomServiceEndpoint {
  type: "custom";
  name: string;
  url: string;
}

export interface EnsServiceEndpoint {
  type: "ens";
  value: string;
}

export interface DidServiceEndpoint {
  type: "did";
  value: string;
}

export interface AgentWalletServiceEndpoint {
  type: "agentWallet";
  value: string;
}

export interface WebServiceEndpoint {
  type: "web";
  url: string;
}

export interface EmailServiceEndpoint {
  type: "email";
  value: string;
}

export type AgentService =
  | McpServiceEndpoint
  | A2aServiceEndpoint
  | OasfServiceEndpoint
  | CustomServiceEndpoint
  | EnsServiceEndpoint
  | DidServiceEndpoint
  | AgentWalletServiceEndpoint
  | WebServiceEndpoint
  | EmailServiceEndpoint;

export interface AgentDomain {
  domain: string;
  primary?: boolean;
}

export interface AgentTrust {
  reputation?: boolean;
  cryptoEconomic?: boolean;
  tee?: boolean;
}

export interface AgentRegistration {
  chainId: string;
  contractAddress: string;
  registeredAt?: string;
}

export interface AgentCard {
  type: string;
  name: string;
  description: string;
  image?: string;
  services?: AgentService[];
  domains?: AgentDomain[];
  capabilities?: {
    skills?: string[];
    domains?: string[];
  };
  trust?: AgentTrust;
  active?: boolean;
  x402?: boolean;
  supportedTrust?: string[];
  metadata?: Record<string, string>;
  registrations?: AgentRegistration[];
  updatedAt?: string;
}

// ---------------------------------------------------------------------------
// Registration type constant
// ---------------------------------------------------------------------------

const ERC8004_REGISTRATION_TYPE =
  "https://eips.ethereum.org/EIPS/eip-8004#registration-v1";

function avatarUrl(name: string): string {
  return `https://api.dicebear.com/9.x/bottts-neutral/svg?seed=${name}`;
}

// ---------------------------------------------------------------------------
// Seed Agent Cards
// ---------------------------------------------------------------------------

export const SEED_AGENT_CARDS: Record<string, AgentCard> = {
  alice: {
    type: ERC8004_REGISTRATION_TYPE,
    name: "alice",
    description:
      "Autonomous USDC yield vault manager specializing in stable yield strategies across Uniswap V3/V4 concentrated liquidity pools with dynamic fee optimization.",
    image: avatarUrl("alice"),
    services: [
      {
        type: "mcp",
        url: "https://localhost:3000/mcp",
        tools: [
          "vault_deposit",
          "vault_withdraw",
          "vault_report",
          "execute_swap",
          "add_liquidity",
          "remove_liquidity",
          "collect_fees",
        ],
        prompts: ["deposit-guide", "strategy-overview"],
        resources: ["vault-status", "position-summary"],
      },
      {
        type: "a2a",
        url: "https://localhost:3001/a2a",
      },
    ],
    domains: [
      { domain: "investment_services", primary: true },
      { domain: "asset_management" },
    ],
    capabilities: {
      skills: [
        "vault-management",
        "yield-optimization",
        "risk-assessment",
        "fee-collection",
        "position-rebalancing",
      ],
      domains: ["defi", "asset-management", "yield-farming"],
    },
    trust: { reputation: true, cryptoEconomic: true, tee: true },
    active: true,
    x402: true,
    supportedTrust: ["reputation", "crypto-economic", "tee"],
    metadata: {
      "protocol:name": "gotts",
      role: "vault_manager",
      "vault:strategy": "stable-yield",
      "vault:asset": "USDC",
      version: "1.0.0",
    },
  },

  bob: {
    type: ERC8004_REGISTRATION_TYPE,
    name: "bob",
    description:
      "WETH growth-focused trading agent using momentum strategies with Uniswap V4 hooks for dynamic fee capture and MEV-protected execution via UniswapX.",
    image: avatarUrl("bob"),
    services: [
      {
        type: "mcp",
        url: "https://localhost:3002/mcp",
        tools: [
          "vault_deposit",
          "vault_withdraw",
          "vault_report",
          "execute_swap",
          "add_liquidity",
          "remove_liquidity",
        ],
      },
      {
        type: "oasf",
        url: "https://localhost:3002/oasf",
        skills: ["market-analysis", "momentum-trading", "risk-monitoring"],
        domains: ["defi", "trading"],
      },
    ],
    domains: [
      { domain: "trading", primary: true },
      { domain: "market_research" },
    ],
    capabilities: {
      skills: [
        "momentum-trading",
        "market-analysis",
        "risk-monitoring",
        "swap-execution",
      ],
      domains: ["defi", "trading", "market-research"],
    },
    trust: { reputation: true, cryptoEconomic: true, tee: false },
    active: true,
    x402: false,
    supportedTrust: ["reputation", "crypto-economic"],
    metadata: {
      "protocol:name": "gotts",
      role: "vault_manager",
      "vault:strategy": "growth-momentum",
      "vault:asset": "WETH",
      version: "1.0.0",
    },
  },

  carol: {
    type: ERC8004_REGISTRATION_TYPE,
    name: "carol",
    description:
      "Conservative multi-asset vault manager focused on capital preservation through diversified LP positions across stable and blue-chip pairs.",
    image: avatarUrl("carol"),
    services: [
      {
        type: "mcp",
        url: "https://localhost:3003/mcp",
        tools: [
          "vault_deposit",
          "vault_withdraw",
          "vault_report",
          "add_liquidity",
          "collect_fees",
        ],
      },
    ],
    domains: [
      { domain: "asset_management", primary: true },
      { domain: "risk_management" },
    ],
    capabilities: {
      skills: [
        "portfolio-diversification",
        "risk-management",
        "capital-preservation",
      ],
      domains: ["defi", "asset-management", "risk-management"],
    },
    trust: { reputation: true, cryptoEconomic: false, tee: false },
    active: true,
    x402: false,
    supportedTrust: ["reputation"],
    metadata: {
      "protocol:name": "gotts",
      role: "vault_manager",
      "vault:strategy": "conservative-diversified",
      "vault:asset": "USDC",
      version: "1.0.0",
    },
  },

  dave: {
    type: ERC8004_REGISTRATION_TYPE,
    name: "dave",
    description:
      "Concentrated liquidity specialist providing tight-range LP positions on high-volume pairs with automated rebalancing and fee compounding.",
    image: avatarUrl("dave"),
    services: [
      {
        type: "mcp",
        url: "https://localhost:3004/mcp",
        tools: [
          "add_liquidity",
          "remove_liquidity",
          "collect_fees",
          "rebalance_position",
          "optimize_lp_range",
          "get_pool_info",
        ],
      },
      {
        type: "a2a",
        url: "https://localhost:3004/a2a",
      },
    ],
    domains: [{ domain: "liquidity_provision", primary: true }],
    capabilities: {
      skills: [
        "concentrated-lp",
        "range-optimization",
        "fee-compounding",
        "position-rebalancing",
      ],
      domains: ["defi", "liquidity-provision"],
    },
    trust: { reputation: true, cryptoEconomic: false, tee: false },
    active: true,
    x402: false,
    supportedTrust: ["reputation"],
    metadata: {
      "protocol:name": "gotts",
      role: "vault_participant",
      "lp:strategy": "concentrated-range",
      version: "1.0.0",
    },
  },

  eve: {
    type: ERC8004_REGISTRATION_TYPE,
    name: "eve",
    description:
      "New agent in observation mode, monitoring market conditions and vault performance before committing capital to active strategies.",
    image: avatarUrl("eve"),
    services: [
      {
        type: "mcp",
        url: "https://localhost:3005/mcp",
        tools: ["get_pool_info", "get_token_price", "get_position"],
      },
    ],
    domains: [{ domain: "investment_services", primary: true }],
    capabilities: {
      skills: ["market-observation", "data-analysis"],
      domains: ["defi", "research"],
    },
    trust: { reputation: true, cryptoEconomic: false, tee: false },
    active: false,
    x402: false,
    supportedTrust: ["reputation"],
    metadata: {
      "protocol:name": "gotts",
      role: "vault_participant",
      status: "observation",
      version: "1.0.0",
    },
  },
};

// ---------------------------------------------------------------------------
// Encoding helper
// ---------------------------------------------------------------------------

/**
 * Encode an AgentCard as a `data:application/json;base64,...` URI.
 *
 * Uses btoa() which works in both Node.js (>=16) and browsers.
 */
export function encodeAgentCardURI(card: AgentCard): string {
  const json = JSON.stringify(card);
  const base64 = btoa(json);
  return `data:application/json;base64,${base64}`;
}
